import type { BaseOptionType } from "antd/es/select"

import { capitalizeFirstLetter } from "../text/capitalize-first-letter.ts"

/**
 * Capitalizes a plain-string option `label` for display. Display-only — the
 * option's `value` is never touched. A label that isn't a string (a React
 * node) is left alone, and a grouped option's own `options` are handled one
 * level down.
 *
 * Returns the original array (and any untouched option inside it) by
 * reference when nothing actually needed capitalizing — the steady state
 * once a list's labels are already properly cased — so this doesn't defeat
 * the memoization one level up in SelectComponent on every render.
 */
export function capitalizeOptionLabels<T extends BaseOptionType>(
  options: T[] | undefined
): T[] | undefined {
  if (!options) return options

  let changed = false
  const next = options.map((option) => {
    if (Array.isArray(option?.options)) {
      const children = capitalizeOptionLabels(option.options)
      const label =
        typeof option.label === "string"
          ? capitalizeFirstLetter(option.label)
          : option.label
      if (children === option.options && label === option.label) return option
      changed = true
      return { ...option, label, options: children }
    }
    if (typeof option?.label === "string") {
      const label = capitalizeFirstLetter(option.label)
      if (label === option.label) return option
      changed = true
      return { ...option, label }
    }
    return option
  })

  return changed ? next : options
}
